import { GridColDef } from '@microservices/wiskey-react-table-component'
import { FormatBold as BoldIcon, FormatItalic as ItalicIcon } from '@mui/icons-material'
import { Box, Grid, Typography } from '@mui/material'

import { hexToHSL } from '@pages/ConfigStyleSettings/helpers/hexToHSL'

import { alignIconsData } from '@components/TextSettings'
import { boxBackground } from '@components/TextSettings/components/ColorSettings'

import { PREVIEW_COLUMNS_FIELD_TYPES } from '@constants'
import { SettingBorder, SettingText } from '@types'

const defaultStyles = {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  width: '100%',
}

const defaultPreviewCellStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px solid black',
  borderRadius: 2,
  minWidth: 30,
  minHeight: 30,
  width: 30,
  height: 30,
  '& svg': {
    fontSize: '20px',
  },
}

const sortPreviewStyleColumns = (v1: string, v2: string) => {
  const isNone1 = v1 === 'none' || !v1
  const isNone2 = v2 === 'none' || !v2

  if (isNone1 && isNone2) return 0

  if (isNone1) return -1

  if (isNone2) return 1

  const color1 = v1
  const color2 = v2

  const hsl1 = hexToHSL(color1)
  const hsl2 = hexToHSL(color2)

  if (hsl1.h !== hsl2.h) return hsl1.h - hsl2.h
  if (hsl1.s !== hsl2.s) return hsl1.s - hsl2.s

  return hsl1.l - hsl2.l
}

const renderPreviewCell = params => {
  const field = params.field

  const { alignment, fontColor, fontFamily, fontSize, isBold, isItalic }: SettingText =
    params.row.settingText

  const { color: borderColor }: SettingBorder = params.row.settingBorder
  const backgroundColor = params.row.background

  switch (field) {
    case PREVIEW_COLUMNS_FIELD_TYPES.EXAMPLE:
      return (
        <Grid sx={defaultStyles}>
          <Box
            sx={{
              ...defaultPreviewCellStyles,
              flex: '1',
              background: backgroundColor,
              border: borderColor ? `2px solid ${borderColor}` : '',
              minWidth: 60,
              height: 30,
              width: 'auto',
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                width: '100%',
                padding: '0 8px',
                textAlign: alignment,
                fontSize: `${fontSize}px`,
                color: fontColor,
                fontFamily,
                fontWeight: isBold ? 'bold' : 'normal',
                fontStyle: isItalic ? 'italic' : 'normal',
              }}
            >
              Example
            </Typography>
          </Box>
        </Grid>
      )
    case PREVIEW_COLUMNS_FIELD_TYPES.TEXT_FORMAT:
      return (
        <Grid gap='4px' sx={defaultStyles}>
          <Box sx={defaultPreviewCellStyles}>{fontSize}</Box>
          <Box sx={{ ...defaultPreviewCellStyles, background: isBold && '#d6e8d5' }}>
            <BoldIcon />
          </Box>
          <Box sx={{ ...defaultPreviewCellStyles, background: isItalic && '#d6e8d5' }}>
            <ItalicIcon />
          </Box>
          <Box sx={defaultPreviewCellStyles}>{alignIconsData[`${alignment}`]}</Box>
        </Grid>
      )
    case PREVIEW_COLUMNS_FIELD_TYPES.TEXT_COLOR:
      return (
        <Grid sx={defaultStyles}>
          <Box
            sx={{
              ...defaultPreviewCellStyles,
              background: fontColor,
            }}
          />
        </Grid>
      )
    case PREVIEW_COLUMNS_FIELD_TYPES.BACKGROUND:
      return (
        <Grid sx={defaultStyles}>
          <Box
            sx={{
              ...defaultPreviewCellStyles,
              border: backgroundColor === 'none' ? 'none' : '1px solid black',
              background: boxBackground(backgroundColor),
            }}
          />
        </Grid>
      )
    case PREVIEW_COLUMNS_FIELD_TYPES.BORDER:
      return (
        <Grid sx={defaultStyles}>
          <Box
            sx={{
              ...defaultPreviewCellStyles,
              border: borderColor === 'none' ? 'none' : '1px solid black',
              background: boxBackground(borderColor),
            }}
          />
        </Grid>
      )
  }
}

export const generateDataGridPreviewColumnsForStyleSettings = (): GridColDef[] => {
  return [
    {
      field: 'example',
      headerName: 'Example',
      flex: 1,
      minWidth: 80,
      headerAlign: 'center',
      sortable: false,
      renderCell: renderPreviewCell,
    },
    {
      field: 'textFormat',
      headerName: 'Text format',
      flex: 1,
      minWidth: 140,
      headerAlign: 'center',
      sortable: false,
      renderCell: renderPreviewCell,
    },
    {
      field: 'textColor',
      headerName: 'Text color',
      flex: 1,
      headerAlign: 'center',
      sortable: false,
      renderCell: renderPreviewCell,
    },
    {
      field: 'background',
      headerName: 'Background',
      flex: 1,
      headerAlign: 'center',
      sortComparator: sortPreviewStyleColumns,
      renderCell: renderPreviewCell,
    },
    {
      field: 'border',
      headerName: 'Border',
      flex: 1,
      headerAlign: 'center',
      sortable: false,
      renderCell: renderPreviewCell,
    },
  ]
}
